import type {
  LaddersLevel,
  LaddersMove,
  LaddersPlayerCount,
  LaddersStyle,
  LaddersView,
} from "@bing/engine-ladders";
import { LADDERS_SOLO_ROOM } from "@bing/shared";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { MatchEndCard, namedMember } from "../components/MatchEndCard";
import { SegmentedChoice } from "../components/SegmentedChoice";
import { StartRow, StartScreenShell } from "../components/StartScreenShell";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import {
  LADDERS_LEVEL_CHOICES,
  LADDERS_PLAYER_CHOICES,
  LADDERS_STYLE_CHOICES,
  LaddersTable,
  laddersCellFigure,
  laddersSeatName,
} from "../screens/LaddersTable";
import {
  getLaddersLevel,
  getLaddersPlayers,
  getLaddersStyle,
  setLaddersLevel,
  setLaddersPlayers,
  setLaddersStyle,
} from "../settings/prefs";
import { useSession } from "../store/context";
import type { GameResult } from "./connection";
import { takeResumeIntent } from "./fresh-solo";
import { createColyseusGameConnector } from "./game-connection";
import { useOutcomeSfx } from "./game-sfx";
import { createGameStore } from "./game-store";
import { useOverlayActive } from "./lifecycle";
import { presetAvatarFor } from "./seat-avatar";
import type { SeatIdentity, SeatPlayer } from "./seat-identity";
import { useTableChannelGate } from "./table-channel-gate";
import { useEndReveal } from "./use-end-reveal";
import { useSoloLoad } from "./use-solo-load";

/**
 * WHY THE MATCH ENDED, in one line — and the BOARD's line only when the board ended it.
 *
 * «رسیدی به ۱۰۰» is said ONLY when a piece stood exactly on ۱۰۰: at a table for two the ROOM can
 * end the match too — the opponent's «خروج» (`"resign"`) or an idle removal (`"idle"`) — and the
 * board, still «playing», has nothing to say about that. The room's reason wins; a reason this
 * bundle does not know names nothing rather than claim a ۱۰۰ nobody reached.
 */
export function laddersEndReason(
  view: Pick<LaddersView, "status" | "winner">,
  result: GameResult,
  won: boolean,
  winnerName: string,
): string | undefined {
  if (result.reason === "resign") return won ? fa.ladders.endOpponentLeft : fa.ladders.endYouLeft;
  if (result.reason === "idle") return won ? fa.ladders.endOpponentIdle : fa.ladders.endYouIdle;
  if (result.reason !== undefined) return undefined;
  if (view.status !== "over" || view.winner === undefined) return undefined;
  return won ? fa.ladders.winSubtitle : fa.ladders.loseSubtitle(winnerName);
}

/** End-of-game card. Reused by the online container. */
export function LaddersEndOverlay({
  view,
  result,
  self,
  players,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  view: LaddersView;
  result: GameResult;
  /** The same identities the table's strips get — your profile, and the online roster. */
  self?: SeatIdentity | null;
  players?: readonly SeatPlayer[];
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call. Undefined ⇒ enabled. */
  againDisabled?: boolean;
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: no «بازی دوباره» at all. */
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const won = result.winners.includes(view.you);
  // The cells the board shows — a partial frame (an old server's, a test's) has none, and then the
  // result's own seats stand in. Never `result.seatScores` as the figure: a room-ended match carries a
  // 1/0 score there, which is no cell at all.
  const cells: readonly number[] | undefined = view.positions;
  const seatCount = cells?.length || result.seatScores.length;
  const nameOf = (seat: number): string => laddersSeatName(seat, view, self, players);
  const winner = result.winners[0];
  // The winner first, then by the cell each one reached — مار پله's own figure.
  const order = Array.from({ length: seatCount }, (_u, seat) => seat).sort(
    (a, b) =>
      Number(result.winners.includes(b)) - Number(result.winners.includes(a)) ||
      (cells?.[b] ?? 0) - (cells?.[a] ?? 0) ||
      a - b,
  );
  const identityOf = (seat: number): SeatIdentity | null =>
    seat === view.you
      ? (self ?? null)
      : ((players ?? []).find((p) => p.seat === seat && p.isBot !== true) ?? null);
  return (
    <MatchEndCard
      label={fa.ladders.gameOver}
      testId="ladders-end"
      outcome={won ? "won" : "lost"}
      glyph="🪜"
      title={won ? fa.ladders.youWin : fa.ladders.youLose}
      reason={laddersEndReason(view, result, won, winner === undefined ? "" : nameOf(winner))}
      sides={order.map((seat) => ({
        key: `seat-${seat}`,
        mine: seat === view.you,
        mark: result.winners.includes(seat) ? "🥇" : "",
        members: [namedMember(seat, identityOf(seat), nameOf(seat), presetAvatarFor(seat))],
        result: cells ? laddersCellFigure(cells[seat] ?? 0) : undefined,
        attrs: { "data-seat": seat },
      }))}
      playAgain={fa.ladders.playAgain}
      leave={fa.ladders.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/**
 * Pre-game START screen — three questions, each one control: how many play (۲ or ۴), the LEVEL
 * (آسان / متوسط / سخت — a set of maps, round 2) and the LOOK (the looks this build can draw). Each
 * is remembered per device. Still NO bot difficulty: every roll is the dice's, so a bot has no
 * decision to make (the planner's call 14) — the level is the BOARD's, never the opponent's.
 */
function LaddersStartScreen({
  players,
  onPlayersChange,
  level,
  onLevelChange,
  look,
  onLookChange,
  onStart,
  onLeave,
}: {
  players: LaddersPlayerCount;
  onPlayersChange: (players: LaddersPlayerCount) => void;
  level: LaddersLevel;
  onLevelChange: (level: LaddersLevel) => void;
  look: LaddersStyle;
  onLookChange: (look: LaddersStyle) => void;
  onStart: () => void;
  onLeave: () => void;
}): ReactNode {
  return (
    <StartScreenShell
      title={fa.ladders.title}
      emoji="🪜"
      heading={fa.soloStart.heading}
      leaveLabel={fa.ladders.leave}
      startLabel={fa.ladders.startGame}
      onStart={onStart}
      onLeave={onLeave}
    >
      <StartRow label={fa.ladders.players}>
        <SegmentedChoice
          label={fa.ladders.players}
          options={LADDERS_PLAYER_CHOICES}
          value={players}
          onChange={onPlayersChange}
          compact
        />
      </StartRow>
      <StartRow label={fa.ladders.level}>
        <SegmentedChoice
          label={fa.ladders.level}
          options={LADDERS_LEVEL_CHOICES}
          value={level}
          onChange={onLevelChange}
          compact
        />
      </StartRow>
      <StartRow label={fa.ladders.look}>
        <SegmentedChoice
          label={fa.ladders.look}
          options={LADDERS_STYLE_CHOICES}
          value={look}
          onChange={onLookChange}
          compact
        />
      </StartRow>
    </StartScreenShell>
  );
}

/** Owns the solo مار پله connection + store for ONE game at a FIXED (locked) size, level and look. */
function LaddersSoloRound({
  players,
  level,
  look,
  onAgain,
  onExit,
}: {
  /** Locked for the whole game — «بازی دوباره» returns to the picker. */
  players: LaddersPlayerCount;
  /** Locked for the game too; a resumed save keeps its own (the server ignores these then). */
  level: LaddersLevel;
  look: LaddersStyle;
  onAgain: () => void;
  onExit: () => void;
}): ReactNode {
  const initData = useSession((s) => s.initDataRaw);
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  // Bumped by the channel gate's «عضو شدم» to re-attempt the join (a fresh store).
  const [round, setRound] = useState(0);
  const store = useMemo(
    () =>
      createGameStore(
        createColyseusGameConnector<LaddersMove, LaddersView>({
          wsUrl: GAME_SERVER_URL,
          room: LADDERS_SOLO_ROOM,
          getInitData: () => initData,
          // The table's size, level and look; the server validates all three. No difficulty is
          // sent: the game has none.
          players,
          laddersLevel: level,
          laddersStyle: look,
        }),
      ),
    [initData, round, players, level, look],
  );
  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  const showEnd = useEndReveal(result !== null);
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      onExit();
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  const { loading, progress } = useSoloLoad(view != null, ready, store);

  const doLeave = (): void => {
    store.getState().disconnect();
    onExit();
  };
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.ladders.connectionError}
          </p>
          <Button variant="primary" onClick={onExit}>
            {fa.ladders.leave}
          </Button>
        </div>
      </div>
    );
  }
  // The connecting splash carries its own exit — never trap the player on it.
  if (loading || !view)
    return (
      <Boot
        caption={fa.ladders.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.ladders.leave}
      />
    );

  return (
    <>
      {/* Once the game is over the exit is the card's «خروج» — there is no game left to keep. */}
      <LaddersTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : () => setConfirmingLeave(true)}
        self={profile}
      />
      {result && showEnd ? (
        <LaddersEndOverlay
          result={result}
          view={view}
          self={profile}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            clearActiveGame("ladders");
            void store
              .getState()
              .endGame()
              .catch(() => {})
              .finally(doLeave);
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
    </>
  );
}

/** Solo مار پله: START screen (the size, level and look, locked) → the game. */
export function LaddersGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  // An explicit «ادامه» from the lobby skips the picker: the saved game already carries the size
  // it began with, and the server would ignore a new pick in favour of it. A fresh start lands here.
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [players, setPlayers] = useState<LaddersPlayerCount>(getLaddersPlayers());
  const [level, setLevel] = useState<LaddersLevel>(getLaddersLevel());
  const [look, setLook] = useState<LaddersStyle>(getLaddersStyle());

  if (phase === "select") {
    return (
      <LaddersStartScreen
        players={players}
        onPlayersChange={(n) => {
          setPlayers(n);
          setLaddersPlayers(n); // remembered per device, like منچ's table size
        }}
        level={level}
        onLevelChange={(l) => {
          setLevel(l);
          setLaddersLevel(l);
        }}
        look={look}
        onLookChange={(l) => {
          setLook(l);
          setLaddersStyle(l);
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      />
    );
  }
  return (
    <LaddersSoloRound
      players={players}
      level={level}
      look={look}
      onAgain={() => setPhase("select")}
      onExit={() => setView("lobby")}
    />
  );
}
